import type { MediaArtifact, MediaCapabilityProfile, RecorderProfile } from '../media/index.ts';
import type { CoasterProject } from '../schema/index.ts';

export const CATALOG_HOVER_DURATION_SECONDS = 8;
export const CATALOG_HOVER_MAX_BYTES = 1_500_000;

export interface CatalogHoverBounds {
  readonly min: { readonly x: number; readonly y: number; readonly z: number };
  readonly max: { readonly x: number; readonly y: number; readonly z: number };
}

export interface CatalogHoverOrbitPlan {
  readonly center: { readonly x: number; readonly y: number; readonly z: number };
  readonly radius: number;
  readonly elevationRadians: number;
  readonly startYawRadians: number;
  readonly verticalFovDegrees: number;
}

export interface CatalogHoverCameraFrame {
  readonly position: { readonly x: number; readonly y: number; readonly z: number };
  readonly target: { readonly x: number; readonly y: number; readonly z: number };
  readonly yawRadians: number;
  readonly frameIndex: number;
}

export interface CatalogHoverVideoArtifact {
  readonly kind: 'catalog_hover_video';
  readonly blob: Blob;
  readonly fileName: string;
  readonly mimeType: string;
  readonly extension: 'mp4' | 'webm';
  readonly width: number;
  readonly height: number;
  readonly fps: number;
  readonly durationSeconds: 8;
  readonly byteSize: number;
  readonly visualFingerprint: string;
  readonly cameraFingerprint: string;
}

export type CatalogHoverCaptureState =
  'waiting_for_preview' | 'generating' | 'ready' | 'unavailable' | 'failed' | 'stale';

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function createCatalogHoverOrbitPlan(
  project: Pick<CoasterProject, 'heroCamera'>,
  bounds: CatalogHoverBounds,
): CatalogHoverOrbitPlan {
  const center = {
    x: (bounds.min.x + bounds.max.x) * 0.5,
    y: (bounds.min.y + bounds.max.y) * 0.5,
    z: (bounds.min.z + bounds.max.z) * 0.5,
  };
  const half = {
    x: Math.max(0.5, (bounds.max.x - bounds.min.x) * 0.5),
    y: Math.max(0.5, (bounds.max.y - bounds.min.y) * 0.5),
    z: Math.max(0.5, (bounds.max.z - bounds.min.z) * 0.5),
  };
  const boundsRadius = Math.max(1, Math.hypot(half.x, half.y, half.z));
  const heroOffset = {
    x: project.heroCamera.position.x - project.heroCamera.target.x,
    y: project.heroCamera.position.y - project.heroCamera.target.y,
    z: project.heroCamera.position.z - project.heroCamera.target.z,
  };
  const heroHorizontal = Math.hypot(heroOffset.x, heroOffset.z);
  const heroDistance = Math.hypot(heroHorizontal, heroOffset.y);
  const elevationRadians = clamp(
    Math.atan2(heroOffset.y, Math.max(0.001, heroHorizontal)),
    (25 * Math.PI) / 180,
    (35 * Math.PI) / 180,
  );
  const verticalFovDegrees = clamp(project.heroCamera.verticalFovDegrees, 36, 62);
  const verticalFov = (verticalFovDegrees * Math.PI) / 180;
  const horizontalFov = 2 * Math.atan(Math.tan(verticalFov / 2) * (16 / 9));
  const fitDistance =
    (boundsRadius / Math.sin(Math.min(verticalFov, horizontalFov) / 2)) *
    (1 + clamp(project.heroCamera.safeMargin, 0.08, 0.4));
  return {
    center,
    // A sphere fit is deliberately conservative because it assumes the tallest
    // and widest extents occur in every camera plane at once. Preserve enough
    // room for the complete bounds while keeping the diorama legible on a
    // small hover card.
    radius: Math.max(fitDistance * 0.72, heroDistance, boundsRadius * 1.7),
    elevationRadians,
    startYawRadians: Math.atan2(heroOffset.z, heroOffset.x),
    verticalFovDegrees,
  };
}

export function catalogHoverCameraFrame(
  plan: CatalogHoverOrbitPlan,
  mediaTimeSeconds: number,
  fps: number,
): CatalogHoverCameraFrame {
  const totalFrames = Math.max(1, Math.round(CATALOG_HOVER_DURATION_SECONDS * fps));
  const frameIndex = clamp(Math.floor(Math.max(0, mediaTimeSeconds) * fps), 0, totalFrames - 1);
  const yawRadians = plan.startYawRadians + (Math.PI * 2 * frameIndex) / totalFrames;
  const horizontalRadius = plan.radius * Math.cos(plan.elevationRadians);
  return {
    position: {
      x: plan.center.x + Math.cos(yawRadians) * horizontalRadius,
      y: plan.center.y + Math.sin(plan.elevationRadians) * plan.radius,
      z: plan.center.z + Math.sin(yawRadians) * horizontalRadius,
    },
    target: plan.center,
    yawRadians,
    frameIndex,
  };
}

function catalogProfile(source: RecorderProfile, quality: 'primary' | 'fallback'): RecorderProfile {
  return {
    ...source,
    width: quality === 'primary' ? 640 : 480,
    height: quality === 'primary' ? 360 : 270,
    fps: quality === 'primary' ? 24 : 20,
    videoBitsPerSecond: quality === 'primary' ? 900_000 : 600_000,
    audioBitsPerSecond: 0,
  };
}

export function catalogHoverRecorderProfiles(
  capabilities: MediaCapabilityProfile,
): readonly RecorderProfile[] {
  if (!capabilities.preferredRecorder) return [];
  return [
    catalogProfile(capabilities.preferredRecorder, 'primary'),
    catalogProfile(capabilities.fallbackRecorder ?? capabilities.preferredRecorder, 'fallback'),
  ];
}

export function catalogHoverArtifactMatches(
  artifact: CatalogHoverVideoArtifact,
  visualFingerprint: string,
  cameraFingerprint: string,
): boolean {
  return (
    artifact.visualFingerprint === visualFingerprint &&
    artifact.cameraFingerprint === cameraFingerprint
  );
}

export function catalogHoverArtifactAsMediaLoop(
  artifact: CatalogHoverVideoArtifact,
): MediaArtifact {
  return {
    kind: 'animated_loop',
    blob: artifact.blob,
    fileName: artifact.fileName.replace('content-hub-preview', 'coaster-loop'),
    mimeType: artifact.mimeType,
    width: artifact.width,
    height: artifact.height,
    durationSeconds: artifact.durationSeconds,
  };
}

export async function captureCatalogHoverWithProfiles<
  Artifact extends { readonly byteSize: number },
>(
  profiles: readonly RecorderProfile[],
  capture: (profile: RecorderProfile) => Promise<Artifact>,
): Promise<Artifact> {
  let failure: unknown = null;
  for (const profile of profiles) {
    try {
      const artifact = await capture(profile);
      if (artifact.byteSize <= CATALOG_HOVER_MAX_BYTES) return artifact;
      failure = new Error('The Buncha Games motion preview exceeded the size limit.');
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
      failure = cause;
    }
  }
  throw failure ?? new Error('The Buncha Games motion preview could not be created.');
}
